/**
 * Determines the last non-activity message between store and API messages.
 * @param {Object} messageInStore - The last non-activity message from the store.
 * @param {Object} messageFromAPI - The last non-activity message from the API.
 * @returns {Object} The latest non-activity message.
 */
const getLastNonActivityMessage = (messageInStore, messageFromAPI) => {
  // If both API value and store value for last non activity message
  // are available, then return the latest one.
  if (messageInStore && messageFromAPI) {
    return messageInStore.created_at >= messageFromAPI.created_at
      ? messageInStore
      : messageFromAPI;
  }
  // Otherwise, return whichever is available
  return messageInStore || messageFromAPI;
};

/**
 * Filters out duplicate source messages from an array of messages.
 * @param {Array} messages - The array of messages to filter.
 * @returns {Array} An array of messages without duplicates.
 */
export const filterDuplicateSourceMessages = (messages = []) => {
  const messagesWithoutDuplicates = [];
  // We cannot use Map or any short hand method as it returns the last message with the duplicate ID
  // We should return the message with smaller id when there is a duplicate
  messages.forEach(m1 => {
    if (m1.source_id) {
      const index = messagesWithoutDuplicates.findIndex(
        m2 => m1.source_id === m2.source_id
      );

      if (index < 0) {
        messagesWithoutDuplicates.push(m1);
      }
    } else {
      messagesWithoutDuplicates.push(m1);
    }
  });
  return messagesWithoutDuplicates;
};

/**
 * Retrieves the last message from a conversation, prioritizing non-activity messages.
 * @param {Object} m - The conversation object containing messages.
 * @returns {Object} The last message of the conversation.
 */
export const getLastMessage = m => {
  const lastMessageIncludingActivity =
    [...m.messages].reverse().find(message => !message.private) ||
    m.messages[m.messages.length - 1];

  const nonActivityMessages = m.messages.filter(
    message => message.message_type !== 2 && !message.private
  );
  const lastNonActivityMessageInStore =
    nonActivityMessages[nonActivityMessages.length - 1];

  const lastNonActivityMessageFromAPI = m.last_non_activity_message;

  // If API value and store value for last non activity message
  // is empty, then return the last activity message
  if (!lastNonActivityMessageInStore && !lastNonActivityMessageFromAPI) {
    return lastMessageIncludingActivity;
  }

  return getLastNonActivityMessage(
    lastNonActivityMessageInStore,
    lastNonActivityMessageFromAPI
  );
};

/**
 * Retrieves the last non-activity message of a conversation, including private notes.
 * @param {Object} conversation - The conversation object containing messages.
 * @returns {Object} The last non-activity message, which may be a private note.
 */
export const getLastMessageIncludingPrivate = conversation => {
  const nonActivityMessages = conversation.messages.filter(
    message => message.message_type !== 2
  );
  const lastNonActivityMessageInStore =
    nonActivityMessages[nonActivityMessages.length - 1];

  // for email conversations the API's last_non_activity_message includes
  // private notes; other channels receive only public messages
  return getLastNonActivityMessage(
    lastNonActivityMessageInStore,
    conversation.last_non_activity_message
  );
};

/**
 * Filters messages that have been read by the agent.
 * @param {Array} messages - The array of messages to filter.
 * @param {number} agentLastSeenAt - The timestamp of when the agent last saw the messages.
 * @returns {Array} An array of read messages.
 */
export const getReadMessages = (messages, agentLastSeenAt) => {
  return messages.filter(
    message => message.created_at * 1000 <= agentLastSeenAt * 1000
  );
};

/**
 * Filters messages that have not been read by the agent.
 * @param {Array} messages - The array of messages to filter.
 * @param {number} agentLastSeenAt - The timestamp of when the agent last saw the messages.
 * @returns {Array} An array of unread messages.
 */
export const getUnreadMessages = (messages, agentLastSeenAt) => {
  return messages.filter(
    message => message.created_at * 1000 > agentLastSeenAt * 1000
  );
};

export const getConversationClassByState = state => {
  switch (state) {
    case 'resolved':
      return 'mail-thin';

    case 'pending':
      return 'robot';

    case 'snoozed':
      return 'alarm-snooze';

    default: // open
      return 'mail-opened';
  }
};

// Picks the conversation to open after the current one. `lastSeen` is where the current
// conversation last sat in the list ({ id, index }): resolving writes the new status before
// the next conversation is picked, and a list filtered by status has dropped the conversation
// by then, so the one after it has moved up into its slot. Returns null when there is none.
export const findNextConversation = (list, currentId, lastSeen) => {
  let currentIndex = list.findIndex(
    conversation => conversation.id === currentId
  );
  let nextIndex = currentIndex + 1;
  if (currentIndex === -1) {
    if (!lastSeen || lastSeen.id !== currentId) return null;
    currentIndex = lastSeen.index;
    nextIndex = currentIndex;
  }

  if (nextIndex < list.length) {
    return { conversation: list[nextIndex], wrapped: false };
  }
  // Past the end: wrap to the top, unless that is the current conversation itself
  if (list.length && list[0].id !== currentId) {
    return { conversation: list[0], wrapped: true };
  }
  return null;
};
